import "./ArticleBadgeControls.scss";
import React, { useCallback, useEffect, useRef } from "react";
import classnames from "classnames";
import { ArticleBadgeDeleteButton } from "./ArticleBadgeDeleteButton";
import { ArticleComment } from "./ArticleComment";
import { ReplacementButton } from "./ReplacementButton";

import type { BettyId, ValidPriceConfigured, Weight } from "../../stores/BaseTypes";
import type { ArticleAvailability, ImageSources, PackagingInfo } from "../../model/ArticleTypes";
import type { ArticlePrices } from "../../model/Prices";
import { AddToCartInput, AddToCartInputRef, EmptyValueStrategy } from "../../shared/add-to-cart/AddToCartInput";
import { convertStateQuantity, normalizeInputQuantity } from "../../utils/QuantityConverter";
import { AmountChooserInfoPanel } from "../../shared/amount-chooser-panel/AmountChooserInfoPanel";

type ArticleBadgeControlsProps = {
	article: ArticleBadgeControlsItemView;
	configuredPrice: ValidPriceConfigured;
	commentEnabled: boolean;
	showComment: boolean;
	onSetAmount: (arg0: BettyId, arg1: number) => void;
	setItemComment?: (bundleId: string, comment: string) => void;
	onDeleteItem: (arg0: BettyId) => void;
	maxQuantityNormal: number;
	maxQuantityWeight: number;
	isSideCaddy?: boolean | null;
	storeName: string | null | undefined;
	hasError?: boolean | null;
};

export type ArticleBadgeControlsItemView = {
	bundleId: string;
	quantity: number;
	comment?: string | null;
	chargedByWeight: boolean;
	weightPerPiece?: Weight | null;
	packaging?: PackagingInfo | null;
	orderable: boolean;
	selectedDeliveryMode?: string | null;
	prices: ArticlePrices | null | undefined;
	unconfirmedState?: boolean;
	imageSources: ImageSources;
	description: string | null | undefined;
	displayId?: string;
	minOrderQuantity?: number | null;
	useMinQuantityMultiplier?: boolean | null;
	availability: ArticleAvailability;
};

/**
 * Component that renders the actions that can be executed on an article badge.
 * Includes deletion of article, changing the amount and adding a comment.
 * It triggers actions directly instead of them being passed in as function props to prevent bloating.
 */
export function ArticleBadgeControls({
	article,
	commentEnabled,
	configuredPrice,
	hasError,
	isSideCaddy,
	maxQuantityNormal,
	maxQuantityWeight,
	onDeleteItem,
	onSetAmount,
	setItemComment,
	showComment,
	storeName,
}: Readonly<ArticleBadgeControlsProps>) {
	const amountInputRef = useRef<AddToCartInputRef>(null);
	const [isFocusOnInputField, setIsFocusOnInputField] = React.useState<boolean>(false);
	const [quantity, setQuantity] = React.useState<number>(article.quantity);

	useEffect(() => {
		setIsFocusOnInputField(false);
	}, [article, setIsFocusOnInputField]);

	const setArticleQuantity = useCallback(
		(value: number) => {
			if (value !== article.quantity) {
				const normalizedValue = normalizeInputQuantity(
					value,
					maxQuantityNormal,
					maxQuantityWeight,
					article.chargedByWeight,
					article.weightPerPiece?.value,
				);
				onSetAmount(article.bundleId, normalizedValue);
			}
		},
		[article, onSetAmount, maxQuantityNormal, maxQuantityWeight],
	);

	const handleInputFocus = useCallback(
		(e: React.SyntheticEvent) => {
			(e.target as HTMLInputElement).select();
			setIsFocusOnInputField(true);
		},
		[setIsFocusOnInputField],
	);

	const handleInputBlur = useCallback(
		(ev: React.FocusEvent) => {
			setIsFocusOnInputField(false);
			if ((ev.target as HTMLInputElement).value === "") {
				amountInputRef.current?.reset();
			}
			if ((ev.target as HTMLInputElement).value === "0") {
				setArticleQuantity(0);
				return;
			}
			setArticleQuantity(quantity);
		},
		[amountInputRef, setIsFocusOnInputField, setArticleQuantity, quantity],
	);

	const handleChangeQuantity = useCallback(
		(quantity: number, action: string) => {
			setQuantity(quantity);
			if (action !== "input") {
				setArticleQuantity(quantity);
			}
		},
		[setArticleQuantity, setQuantity],
	);

	const primaryPrice = configuredPrice.valid ? configuredPrice.positionPrices.primary.amount : null;
	const minValue = article.minOrderQuantity ?? 0;
	return (
		<div className={classnames("article-buttons", { reduced: !showComment })}>
			{!isSideCaddy && (
				<ArticleBadgeDeleteButton article={article} onDeleteItem={onDeleteItem} hasError={hasError} />
			)}
			{showComment && (
				<ArticleComment
					data-selenium="article-comment"
					disabled={!commentEnabled}
					comment={article.comment ?? undefined}
					setItemComment={setItemComment ?? (() => {})}
					bundleId={article.bundleId}
				/>
			)}
			<div className="article-actions-amount">
				<div className="mfcss_amountchooser">
					<AddToCartInput
						id={`amount-input-${article.bundleId}`}
						ref={amountInputRef}
						onFocus={handleInputFocus}
						onBlur={handleInputBlur}
						initialValue={convertStateQuantity(
							article.quantity,
							article.chargedByWeight,
							article.weightPerPiece?.value,
						)}
						isDecimalInput={article.chargedByWeight && !article.weightPerPiece?.value}
						isDisabled={false}
						hasStepperInput
						emptyValueStrategy={EmptyValueStrategy.IGNORE}
						onChangeQuantity={handleChangeQuantity}
						multiplier={article.useMinQuantityMultiplier && minValue > 1 ? minValue : 1}
					/>
				</div>
				{
					// FIXME: Use unit from weightPerPiece here, once this actually comes from
					// the article data itself (and not from EV).
					!!article.weightPerPiece?.value && !isSideCaddy && (
						<AmountChooserInfoPanel
							totalWeight={Math.round(article.quantity * 100) / 100}
							weightPerPieceValue={article.weightPerPiece?.value}
							packagingTypeId={article.packaging?.typeId}
							totalPrice={primaryPrice?.amount}
							currency={primaryPrice?.currency}
							isPriceLoading={article.unconfirmedState}
							showPermanently={isFocusOnInputField}
						/>
					)
				}
			</div>
			{isSideCaddy && <ArticleBadgeDeleteButton article={article} onDeleteItem={onDeleteItem} isSideCaddy />}
			{!isSideCaddy && article.availability !== "AVAILABLE" && (
				<ReplacementButton article={article} storeName={storeName} />
			)}
		</div>
	);
}
